import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { assistentes as apiAssistentes, type AssistenteNaLista } from '../api/assistentes'
import { roteamento as api, type Roteamento as Dados } from '../api/roteamento'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { PendenciasDePublicacao } from '../componentes/PendenciasDePublicacao'
import { useSessao } from '../auth/useSessao'
import { mensagemDeErro } from './mensagemDeErro'
import './usuarios.css'
import './roteamento.css'
import { marcaDaRota } from './marcaDaRota'


/**
 * Para onde vai cada motivo de transbordo.
 *
 * A tabela já existia no banco, e era editável só por `UPDATE`. O efeito prático
 * é que ninguém a editava — o Guilherme não abre o Postgres —, e a consequência
 * de não editar é a conversa cair no time errado, que é onde ela fica parada.
 */
export function Roteamento() {
  const { t } = useTranslation()
  const { pode } = useSessao()

  const marca = (marcaDaRota() ?? '')
  const editavel = pode('ParametroGerenciar')

  const [dados, setDados] = useState<Dados | null>(null)
  const [assistentes, setAssistentes] = useState<AssistenteNaLista[]>([])
  const [assistente, setAssistente] = useState<number | null>(null)
  const [times, setTimes] = useState('')
  const [destinos, setDestinos] = useState<Record<string, string>>({})
  const [falhou, setFalhou] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [recado, setRecado] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  // Recarrega o aviso da 123 a cada gravação — ver `Regras.tsx`.
  const [gravacoes, setGravacoes] = useState(0)

  const carregar = useCallback(async () => {
    setFalhou(false)
    try {
      const [config, quais] = await Promise.all([
        api.ler(marca, assistente ?? undefined),
        apiAssistentes.listar(marca),
      ])

      setDados(config)
      setAssistentes(quais)
      setTimes(config.times.join(', '))
      setDestinos(Object.fromEntries(config.linhas.map((l) => [l.motivo, l.time])))
    } catch {
      setFalhou(true)
    }
  }, [marca, assistente])

  useEffect(() => {
    void carregar()
  }, [carregar])

  if (falhou)
    return <Estado tipo="erro" acao={<Botao onClick={carregar}>{t('comum.tentarDeNovo')}</Botao>} />

  if (!dados) return <Estado tipo="carregando" />

  const listaDeTimes = times
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)

  async function salvar(evento: FormEvent) {
    evento.preventDefault()

    setErro(null)
    setRecado(null)
    setEnviando(true)

    try {
      const resultado = await api.salvar(marca, assistente, listaDeTimes, destinos)

      setRecado(
        resultado.alteradas === 0
          ? t('roteamento.semMudanca')
          : t('roteamento.salvo', { total: resultado.alteradas }),
      )

      await carregar()
      setGravacoes((n) => n + 1)
    } catch (e) {
      setErro(mensagemDeErro(e, t))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <main className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('roteamento.titulo')}</h1>
          <p className="usuarios__descricao">{t('roteamento.descricao')}</p>
        </div>
      </header>

      {/* O roteamento vive no mesmo retrato publicado que as regras: gravar
          para onde vai um motivo de transbordo não muda nada até publicar (123). */}
      <PendenciasDePublicacao recarregar={gravacoes} />

      {erro && <Aviso tom="perigo">{erro}</Aviso>}
      {recado && <Aviso tom="sucesso">{recado}</Aviso>}
      {!editavel && <Aviso tom="info">{t('assistentes.somenteLeitura')}</Aviso>}

      <form onSubmit={salvar} noValidate>
        <Cartao titulo={t('roteamento.times')}>
          {/* 🔴 A lista fechada é o que impede o assistente de inventar
              departamento. Ela vira o enum do schema da ferramenta — a regra se
              resolve no schema, não pedindo ao modelo que se comporte. */}
          <Aviso tom="info">{t('roteamento.timesExplicacao')}</Aviso>

          <Campo
            rotulo={t('roteamento.timesRotulo')}
            value={times}
            onChange={(e) => setTimes(e.target.value)}
            disabled={!editavel}
            ajuda={t('roteamento.timesAjuda')}
            obrigatorio
          />

          {!dados.timesConfigurados && (
            <Aviso tom="alerta">{t('roteamento.timesPadrao')}</Aviso>
          )}
        </Cartao>

        <Cartao titulo={t('roteamento.destinos')}>
          <label className="usuarios__filtro">
            <span>{t('roteamento.escopo')}</span>
            <select
              value={assistente ?? ''}
              onChange={(e) => setAssistente(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">{t('roteamento.todosOsAssistentes')}</option>
              {assistentes.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nome}
                </option>
              ))}
            </select>
          </label>

          <div
              className="usuarios__tabela-rolagem"
              // A região rolável precisa ser alcançável pelo teclado (WCAG 2.1.1):
              // sem isto, a parte escondida da tabela não existe para quem não usa mouse.
              tabIndex={0}
              role="group"
              aria-label={t('roteamento.legenda')}
            >
              <table className="usuarios__tabela">
              <caption className="usuarios__legenda">{t('roteamento.legenda')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('roteamento.quando')}</th>
                  <th scope="col">{t('roteamento.vaiPara')}</th>
                </tr>
              </thead>
              <tbody>
                {dados.linhas.map((linha) => (
                  <tr key={linha.motivo}>
                    <th scope="row" className="roteamento__motivo">
                      {linha.rotulo}
                      {/* "Padrão" não é decoração: sem ele, a tela sugere que
                          alguém escolheu aquele destino, quando é recuo do
                          código — e ninguém revisa o que parece decidido. */}
                      {linha.padrao && (
                        <span className="etiqueta etiqueta--pendente">
                          {t('roteamento.padrao')}
                        </span>
                      )}
                    </th>
                    <td>
                      <select
                        value={destinos[linha.motivo] ?? ''}
                        disabled={!editavel}
                        aria-label={t('roteamento.destinoDe', { motivo: linha.rotulo })}
                        onChange={(e) =>
                          setDestinos({ ...destinos, [linha.motivo]: e.target.value })
                        }
                      >
                        {listaDeTimes.map((time) => (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        ))}

                        {/* O destino atual pode não estar na lista editada — e
                            mostrar vazio faria parecer que ele não existe.
                            Salvar recusa; a tela precisa deixar isso visível. */}
                        {destinos[linha.motivo] &&
                          !listaDeTimes.includes(destinos[linha.motivo]) && (
                            <option value={destinos[linha.motivo]}>
                              {t('roteamento.foraDaLista', { time: destinos[linha.motivo] })}
                            </option>
                          )}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {editavel && (
            <div className="usuarios__acoes-formulario">
              <Botao type="submit" variante="primario" carregando={enviando}>
                {t('comum.salvar')}
              </Botao>
            </div>
          )}
        </Cartao>
      </form>
    </main>
  )
}
